import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/SectionHeading";
import { quoteByIdQuery } from "@/lib/query-registry";

export const Route = createFileRoute("/quote/$id")({
  head: () => ({
    meta: [
      { title: "Your quote: Shugert" },
      { name: "description", content: "Your Shugert project quote." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  loader: async ({ params, context }) => {
    const res = await context.queryClient.ensureQueryData(quoteByIdQuery(params.id));
    if (!res.quote) throw notFound();
    return res.quote;
  },
  errorComponent: ({ error }) => (
    <SiteShell>
      <section className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-semibold text-foreground">Quote not available</h1>
        <p className="mt-3 text-muted-foreground">{error.message}</p>
        <Link to="/" className="mt-6 inline-block text-sm text-foreground underline">Go home</Link>
      </section>
    </SiteShell>
  ),
  notFoundComponent: () => (
    <SiteShell>
      <section className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-semibold text-foreground">Quote not found</h1>
        <Link to="/" className="mt-6 inline-block text-sm text-foreground underline">Go home</Link>
      </section>
    </SiteShell>
  ),
  component: QuoteView,
});

function QuoteView() {
  const q = Route.useLoaderData();
  const isEs = q.locale === "es";
  const t = isEs
    ? {
        eyebrow: "Tu cotización",
        title: "Gracias: tenemos lo que necesitamos.",
        body: "Revisaremos tu tienda y te enviaremos un alcance claro en menos de 48 horas.",
        services: "Servicios", timeline: "Timeline",
        closing: "Solo aceptamos proyectos donde vemos impacto claro. Si no somos el equipo correcto para ti, te lo diremos de frente.",
      }
    : {
        eyebrow: "Your quote",
        title: "Thanks: we've got what we need.",
        body: "We'll review your store and send a clear scope within 48 hours.",
        services: "Services", timeline: "Timeline",
        closing: "We only take on projects where we see clear impact. If we're not the right fit, we'll tell you upfront.",
      };
  return (
    <SiteShell>
      <section className="mx-auto max-w-3xl px-6 py-24">
        {/* Not wrapped in <Reveal>: renders the page H1, the LCP text
            candidate, and Reveal renders opacity:0 until client hydration +
            IntersectionObserver, which delays LCP. */}
        <SectionHeading as="h1" eyebrow={t.eyebrow} title={t.title} description={t.body} />
        <Reveal delay={80}>
          <dl className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-2">
            <Item label={t.services} value={(q.services ?? []).join(", ") || ", "} />
            <Item label={t.timeline} value={q.timeline || ", "} />
          </dl>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-10 text-center text-sm italic leading-relaxed text-muted-foreground">{t.closing}</p>
        </Reveal>
      </section>
    </SiteShell>
  );
}

function Item({ label, value, full }: { label: string; value: string; full?: boolean }) {
  return (
    <div className={`bg-background p-6 ${full ? "sm:col-span-2" : ""}`}>
      <dt className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</dt>
      <dd className="mt-2 text-sm text-foreground">{value}</dd>
    </div>
  );
}
